import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

const SIZE_W = { sm: '400px', md: '560px', lg: '720px' };

// Modal — generic dialog wrapper.
// variant: 'default' | 'dangerous'
// size: 'sm' | 'md' | 'lg'
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  size = 'md',
  variant = 'default',
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 bg-sitruna-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          style={{ zIndex: 'var(--z-modal)' }}
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-32px)] max-h-[90vh] flex flex-col bg-white rounded-xl shadow-3 focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          style={{ maxWidth: SIZE_W[size], zIndex: 'var(--z-modal)' }}
        >
          {/* Header */}
          <div className={`shrink-0 flex items-center justify-between px-5 py-4 border-b border-n-100 ${variant === 'dangerous' ? 'border-status-bad/20' : ''}`}>
            <Dialog.Title className={`text-[15px] font-semibold ${variant === 'dangerous' ? 'text-status-bad' : 'text-n-800'}`}>
              {title}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                className="w-7 h-7 flex items-center justify-center rounded text-n-400 hover:text-n-700 hover:bg-n-50 transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                </svg>
              </button>
            </Dialog.Close>
          </div>

          {/* Body — scrollable */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {description && (
              <Dialog.Description className="text-sm text-n-500 mb-4">
                {description}
              </Dialog.Description>
            )}
            {!description && <VisuallyHidden><Dialog.Description>Modal content</Dialog.Description></VisuallyHidden>}
            {children}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ModalFooter — standardised action row
export function ModalFooter({ children }) {
  return (
    <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-n-100 bg-n-25 rounded-b-xl">
      {children}
    </div>
  );
}

// ModalClose — convenience wrapper
export function ModalClose({ children }) {
  return <Dialog.Close asChild>{children}</Dialog.Close>;
}
